import { adminApi } from "@/lib/adminApi";

const NEWS_CATEGORIES = [
    "Driver News",
    "Team News",
    "Technical Updates",
    "Race Weekend",
    "Strategy",
    "Transfers",
    "Contracts",
    "Regulation Changes",
    "Championship",
    "Race Results",
];

const DRIVER_STATUSES = ["active", "inactive", "retired", "legend"];
const TEAM_STATUSES = ["active", "inactive", "historical", "legendary"];
const CALENDAR_STATUSES = ["upcoming", "live", "completed", "delayed", "cancelled"];
const NEWS_STATUSES = ["draft", "published", "hidden"];
const SESSION_TYPES = ["FP1", "FP2", "FP3", "Sprint Shootout", "Sprint", "Qualifying", "Race"];
const RECORD_HOLDER_TYPES = ["driver", "team"];

export const adminNavItems = [
    { to: "/admin", label: "Overview" },
    { to: "/admin/news", label: "News" },
    { to: "/admin/drivers", label: "Drivers" },
    { to: "/admin/teams", label: "Teams" },
    { to: "/admin/calendar", label: "Calendar" },
    { to: "/admin/stats", label: "Stats" },
    { to: "/admin/records", label: "Records" },
    { to: "/admin/glossary", label: "Glossary" },
    { to: "/admin/featured", label: "Featured" },
    { to: "/admin/providers", label: "Providers" },
    { to: "/admin/operations", label: "Operations" },
    { to: "/admin/race-engine", label: "Race Engine" },
    { to: "/admin/sync", label: "Sync" },
    { to: "/admin/audit-logs", label: "Audit Logs" },
    { to: "/admin/settings", label: "Settings" },
];

function isoToLocalValue(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const pad = (num) => String(num).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function localToIsoValue(value) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString();
}

function arrayToText(value) {
    return Array.isArray(value) ? value.join(", ") : "";
}

function textToArray(value) {
    return String(value || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
}

export const entityConfigs = {
    news: {
        key: "news",
        api: adminApi.news,
        label: "News",
        singular: "Article",
        description: "Create, publish, feature, and manage race-briefing content.",
        listFilters: [
            { name: "status", label: "Status", options: NEWS_STATUSES },
            { name: "category", label: "Category", options: NEWS_CATEGORIES },
        ],
        columns: [
            { key: "title", label: "Title" },
            { key: "category", label: "Category" },
            { key: "status", label: "Status", type: "badge" },
            { key: "featured", label: "Featured", type: "boolean" },
            { key: "published_at", label: "Published", type: "date" },
        ],
        fields: [
            { name: "title", label: "Title", type: "text", required: true },
            { name: "slug", label: "Slug", type: "text", required: true },
            { name: "category", label: "Category", type: "select", options: NEWS_CATEGORIES, required: true },
            { name: "status", label: "Status", type: "select", options: NEWS_STATUSES, required: true },
            { name: "featured", label: "Featured", type: "checkbox" },
            { name: "source", label: "Source", type: "text" },
            { name: "source_url", label: "Source URL", type: "text" },
            { name: "image_url", label: "Image URL", type: "text" },
            { name: "published_at", label: "Published At", type: "datetime" },
            { name: "tags", label: "Tags", type: "array", help: "Comma-separated tags." },
            { name: "related_driver_ids", label: "Related Driver IDs", type: "array", help: "Comma-separated driver ids." },
            { name: "related_team_ids", label: "Related Team IDs", type: "array", help: "Comma-separated team ids." },
            { name: "related_race_id", label: "Related Race ID", type: "text" },
            { name: "summary", label: "Summary", type: "textarea", required: true },
            { name: "body", label: "Body", type: "textarea", required: true },
        ],
        defaults: {
            title: "",
            slug: "",
            summary: "",
            body: "",
            source: "",
            source_url: "",
            image_url: "",
            category: "Race Weekend",
            tags: "",
            related_driver_ids: "",
            related_team_ids: "",
            related_race_id: "",
            published_at: "",
            status: "draft",
            featured: false,
        },
        toFormValues(item) {
            return {
                ...this.defaults,
                ...item,
                tags: arrayToText(item?.tags),
                related_driver_ids: arrayToText(item?.related_driver_ids),
                related_team_ids: arrayToText(item?.related_team_ids),
                published_at: isoToLocalValue(item?.published_at),
            };
        },
        toApiPayload(form) {
            return {
                title: form.title,
                slug: form.slug,
                summary: form.summary,
                body: form.body,
                source: form.source,
                source_url: form.source_url,
                image_url: form.image_url,
                category: form.category,
                tags: textToArray(form.tags),
                related_driver_ids: textToArray(form.related_driver_ids),
                related_team_ids: textToArray(form.related_team_ids),
                related_race_id: form.related_race_id || null,
                published_at: localToIsoValue(form.published_at),
                status: form.status,
                featured: Boolean(form.featured),
            };
        },
    },
    drivers: {
        key: "drivers",
        api: adminApi.drivers,
        label: "Drivers",
        singular: "Driver",
        description: "Manage driver bios, statuses, team assignment, and headline stats.",
        listFilters: [{ name: "status", label: "Status", options: DRIVER_STATUSES }],
        columns: [
            { key: "display_name", label: "Driver" },
            { key: "team_id", label: "Team" },
            { key: "active_status", label: "Status", type: "badge" },
            { key: "points", label: "Points" },
            { key: "wins", label: "Wins" },
        ],
        fields: [
            { name: "first_name", label: "First Name", type: "text", required: true },
            { name: "last_name", label: "Last Name", type: "text", required: true },
            { name: "display_name", label: "Display Name", type: "text", required: true },
            { name: "slug", label: "Slug", type: "text", required: true },
            { name: "number", label: "Number", type: "number" },
            { name: "abbreviation", label: "Abbreviation", type: "text" },
            { name: "team_id", label: "Team ID", type: "text" },
            { name: "country", label: "Country", type: "text" },
            { name: "active_status", label: "Status", type: "select", options: DRIVER_STATUSES, required: true },
            { name: "image_url", label: "Image URL", type: "text" },
            { name: "career_start_year", label: "Career Start Year", type: "number" },
            { name: "championships", label: "Championships", type: "number" },
            { name: "wins", label: "Wins", type: "number" },
            { name: "podiums", label: "Podiums", type: "number" },
            { name: "poles", label: "Poles", type: "number" },
            { name: "fastest_laps", label: "Fastest Laps", type: "number" },
            { name: "points", label: "Points", type: "number" },
            { name: "current_season_position", label: "Season Position", type: "number" },
            { name: "bio", label: "Bio", type: "textarea" },
        ],
        defaults: {
            first_name: "",
            last_name: "",
            display_name: "",
            slug: "",
            number: "",
            abbreviation: "",
            team_id: "",
            country: "",
            active_status: "active",
            image_url: "",
            bio: "",
            career_start_year: "",
            championships: 0,
            wins: 0,
            podiums: 0,
            poles: 0,
            fastest_laps: 0,
            points: 0,
            current_season_position: "",
        },
        toFormValues(item) {
            return { ...this.defaults, ...item };
        },
        toApiPayload(form) {
            return {
                first_name: form.first_name,
                last_name: form.last_name,
                display_name: form.display_name,
                slug: form.slug,
                number: form.number === "" ? null : Number(form.number),
                abbreviation: form.abbreviation,
                team_id: form.team_id || null,
                country: form.country,
                active_status: form.active_status,
                image_url: form.image_url,
                bio: form.bio,
                career_start_year: form.career_start_year === "" ? null : Number(form.career_start_year),
                championships: Number(form.championships || 0),
                wins: Number(form.wins || 0),
                podiums: Number(form.podiums || 0),
                poles: Number(form.poles || 0),
                fastest_laps: Number(form.fastest_laps || 0),
                points: Number(form.points || 0),
                current_season_position:
                    form.current_season_position === "" ? null : Number(form.current_season_position),
            };
        },
    },
    teams: {
        key: "teams",
        api: adminApi.teams,
        label: "Teams",
        singular: "Team",
        description: "Manage constructor identity, colors, driver links, and season totals.",
        listFilters: [{ name: "status", label: "Status", options: TEAM_STATUSES }],
        columns: [
            { key: "name", label: "Team" },
            { key: "country", label: "Country" },
            { key: "active_status", label: "Status", type: "badge" },
            { key: "points", label: "Points" },
            { key: "constructor_position", label: "Pos" },
        ],
        fields: [
            { name: "name", label: "Name", type: "text", required: true },
            { name: "full_name", label: "Full Name", type: "text", required: true },
            { name: "slug", label: "Slug", type: "text", required: true },
            { name: "country", label: "Country", type: "text" },
            { name: "base", label: "Base", type: "text" },
            { name: "active_status", label: "Status", type: "select", options: TEAM_STATUSES, required: true },
            { name: "logo_url", label: "Logo URL", type: "text" },
            { name: "primary_color", label: "Primary Color", type: "text" },
            { name: "secondary_color", label: "Secondary Color", type: "text" },
            { name: "drivers", label: "Driver IDs", type: "array", help: "Comma-separated driver ids." },
            { name: "championships", label: "Championships", type: "number" },
            { name: "wins", label: "Wins", type: "number" },
            { name: "podiums", label: "Podiums", type: "number" },
            { name: "poles", label: "Poles", type: "number" },
            { name: "points", label: "Points", type: "number" },
            { name: "constructor_position", label: "Constructor Position", type: "number" },
            { name: "bio", label: "Bio", type: "textarea" },
        ],
        defaults: {
            name: "",
            full_name: "",
            slug: "",
            country: "",
            base: "",
            active_status: "active",
            logo_url: "",
            primary_color: "#E10600",
            secondary_color: "#FFFFFF",
            drivers: "",
            championships: 0,
            wins: 0,
            podiums: 0,
            poles: 0,
            points: 0,
            constructor_position: "",
            bio: "",
        },
        toFormValues(item) {
            return { ...this.defaults, ...item, drivers: arrayToText(item?.drivers) };
        },
        toApiPayload(form) {
            return {
                name: form.name,
                full_name: form.full_name,
                slug: form.slug,
                country: form.country,
                base: form.base,
                active_status: form.active_status,
                logo_url: form.logo_url,
                primary_color: form.primary_color,
                secondary_color: form.secondary_color,
                drivers: textToArray(form.drivers),
                championships: Number(form.championships || 0),
                wins: Number(form.wins || 0),
                podiums: Number(form.podiums || 0),
                poles: Number(form.poles || 0),
                points: Number(form.points || 0),
                constructor_position:
                    form.constructor_position === "" ? null : Number(form.constructor_position),
                bio: form.bio,
            };
        },
    },
    calendar: {
        key: "calendar",
        api: adminApi.calendar,
        label: "Calendar",
        singular: "Race Weekend",
        description: "Manage race weekends, statuses, and session times.",
        listFilters: [{ name: "status", label: "Status", options: CALENDAR_STATUSES }],
        columns: [
            { key: "race_name", label: "Race" },
            { key: "season", label: "Season" },
            { key: "round", label: "Round" },
            { key: "country", label: "Country" },
            { key: "status", label: "Status", type: "badge" },
        ],
        fields: [
            { name: "season", label: "Season", type: "number", required: true },
            { name: "round", label: "Round", type: "number", required: true },
            { name: "race_name", label: "Race Name", type: "text", required: true },
            { name: "official_name", label: "Official Name", type: "text" },
            { name: "slug", label: "Slug", type: "text", required: true },
            { name: "circuit_name", label: "Circuit", type: "text", required: true },
            { name: "country", label: "Country", type: "text", required: true },
            { name: "city", label: "City", type: "text", required: true },
            { name: "timezone", label: "Timezone", type: "text", required: true },
            { name: "weekend_start_date", label: "Weekend Start", type: "text", required: true },
            { name: "weekend_end_date", label: "Weekend End", type: "text", required: true },
            { name: "race_start_time_utc", label: "Race Start (UTC)", type: "datetime", required: true },
            { name: "status", label: "Status", type: "select", options: CALENDAR_STATUSES, required: true },
            { name: "source_provider", label: "Source Provider", type: "text" },
            { name: "source_url", label: "Source URL", type: "text" },
            { name: "sessions", label: "Sessions", type: "sessions" },
        ],
        defaults: {
            season: new Date().getFullYear(),
            round: "",
            race_name: "",
            official_name: "",
            slug: "",
            circuit_name: "",
            country: "",
            city: "",
            timezone: "UTC",
            weekend_start_date: "",
            weekend_end_date: "",
            race_start_time_utc: "",
            status: "upcoming",
            source_provider: "",
            source_url: "",
            sessions: [],
        },
        toFormValues(item) {
            return {
                ...this.defaults,
                ...item,
                race_start_time_utc: isoToLocalValue(item?.race_start_time_utc),
                sessions: (item?.sessions || []).map((session) => ({
                    ...session,
                    start_time_utc: isoToLocalValue(session.start_time_utc),
                    end_time_utc: isoToLocalValue(session.end_time_utc),
                })),
            };
        },
        toApiPayload(form) {
            return {
                season: Number(form.season),
                round: Number(form.round),
                race_name: form.race_name,
                official_name: form.official_name,
                slug: form.slug,
                circuit_name: form.circuit_name,
                country: form.country,
                city: form.city,
                timezone: form.timezone,
                weekend_start_date: form.weekend_start_date,
                weekend_end_date: form.weekend_end_date,
                race_start_time_utc: localToIsoValue(form.race_start_time_utc),
                status: form.status,
                source_provider: form.source_provider,
                source_url: form.source_url,
                sessions: (form.sessions || []).map((session) => ({
                    id: session.id || undefined,
                    session_type: session.session_type,
                    session_name: session.session_name,
                    start_time_utc: localToIsoValue(session.start_time_utc),
                    end_time_utc: localToIsoValue(session.end_time_utc),
                    status: session.status,
                })),
            };
        },
        meta: {
            sessionTypes: SESSION_TYPES,
            sessionStatuses: CALENDAR_STATUSES,
        },
    },
    records: {
        key: "records",
        api: adminApi.records,
        label: "Records",
        singular: "Record",
        description: "Manage stat records and featured milestones.",
        listFilters: [{ name: "holder_type", label: "Holder Type", options: RECORD_HOLDER_TYPES }],
        columns: [
            { key: "title", label: "Title" },
            { key: "category", label: "Category" },
            { key: "holder_type", label: "Holder", type: "badge" },
            { key: "value", label: "Value" },
            { key: "featured", label: "Featured", type: "boolean" },
        ],
        fields: [
            { name: "title", label: "Title", type: "text", required: true },
            { name: "category", label: "Category", type: "text", required: true },
            { name: "holder_type", label: "Holder Type", type: "select", options: RECORD_HOLDER_TYPES, required: true },
            { name: "holder_id", label: "Holder ID", type: "text", required: true },
            { name: "value", label: "Value", type: "text", required: true },
            { name: "season", label: "Season", type: "number" },
            { name: "featured", label: "Featured", type: "checkbox" },
            { name: "description", label: "Description", type: "textarea" },
        ],
        defaults: {
            title: "",
            category: "",
            holder_type: "driver",
            holder_id: "",
            value: "",
            season: "",
            featured: false,
            description: "",
        },
        toFormValues(item) {
            return { ...this.defaults, ...item };
        },
        toApiPayload(form) {
            return {
                title: form.title,
                category: form.category,
                holder_type: form.holder_type,
                holder_id: form.holder_id,
                value: form.value,
                season: form.season === "" ? null : Number(form.season),
                featured: Boolean(form.featured),
                description: form.description,
            };
        },
    },
    glossary: {
        key: "glossary",
        api: adminApi.glossary,
        label: "Glossary",
        singular: "Term",
        description: "Manage Learn F1 glossary and definition content.",
        listFilters: [],
        columns: [
            { key: "term", label: "Term" },
            { key: "category", label: "Category" },
            { key: "slug", label: "Slug" },
            { key: "updated_at", label: "Updated", type: "date" },
        ],
        fields: [
            { name: "term", label: "Term", type: "text", required: true },
            { name: "slug", label: "Slug", type: "text", required: true },
            { name: "category", label: "Category", type: "text", required: true },
            { name: "simple_definition", label: "Simple Definition", type: "textarea", required: true },
            { name: "detailed_explanation", label: "Detailed Explanation", type: "textarea", required: true },
            { name: "example", label: "Example", type: "textarea" },
        ],
        defaults: {
            term: "",
            slug: "",
            category: "",
            simple_definition: "",
            detailed_explanation: "",
            example: "",
        },
        toFormValues(item) {
            return { ...this.defaults, ...item };
        },
        toApiPayload(form) {
            return {
                term: form.term,
                slug: form.slug,
                category: form.category,
                simple_definition: form.simple_definition,
                detailed_explanation: form.detailed_explanation,
                example: form.example,
            };
        },
    },
};
